import { X, MessageCircle, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { Timestamp } from 'firebase/firestore';
import { ImageViewer } from '@/components/ImageViewer';
import { useBodyOverflowLock } from '@/hooks/useBodyOverflowLock';

interface FavoriteMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  author: string;
  text: string;
  timestamp?: Timestamp;
  imageUrl?: string;
  imageUrls?: string[];
  chatSource?: 'community' | 'leaders';
  onNavigateToChat?: () => void;
}

export function FavoriteMessageModal({
  isOpen,
  onClose,
  author,
  text,
  timestamp,
  imageUrl,
  imageUrls,
  chatSource,
  onNavigateToChat,
}: FavoriteMessageModalProps) {
  const [viewingImage, setViewingImage] = useState<string | null>(null);

  // Get all images (prefer imageUrls array, fallback to single imageUrl)
  const singleImageFallback = imageUrl ? [imageUrl] : [];
  const images = imageUrls?.length ? imageUrls : singleImageFallback;

  // Lock body overflow when modal is open
  useBodyOverflowLock(isOpen);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const formatDate = (ts?: Timestamp) => {
    if (!ts?.toDate) return '';
    const date = ts.toDate();
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`p-6 text-white ${chatSource === 'leaders' ? 'bg-gradient-to-r from-[#8e4585] to-[#a855a0]' : 'bg-gradient-to-r from-green-400 to-green-500'}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-3 rounded-xl">
                <MessageCircle size={24} />
              </div>
              <div>
                <span className="text-white/80 text-sm font-medium">
                  {chatSource === 'leaders' ? 'Leaders Chat' : 'Community Post'}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Author */}
          <div className="flex items-center gap-2 mb-4">
            <div className="bg-slate-100 p-2 rounded-full">
              <User size={16} className="text-slate-500" />
            </div>
            <div>
              <p className="font-medium text-slate-800">{author}</p>
              {timestamp && <p className="text-xs text-slate-400">{formatDate(timestamp)}</p>}
            </div>
          </div>

          {/* Image attachments */}
          {images.length > 0 && (
            <div className={`mb-4 ${images.length === 1 ? '' : 'grid grid-cols-2 gap-2'}`}>
              {images.map((url, idx) => (
                <button
                  key={url}
                  onClick={() => setViewingImage(url)}
                  className="block rounded-xl overflow-hidden hover:opacity-90 transition-opacity cursor-zoom-in"
                >
                  <img
                    src={url}
                    alt={`Attachment ${idx + 1}`}
                    className={`w-full object-cover ${images.length === 1 ? 'max-h-64' : 'h-32'}`}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Message text */}
          {text && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{text}</p>
            </div>
          )}
        </div>

        {/* Image viewer */}
        {viewingImage && (
          <ImageViewer
            src={viewingImage}
            alt="Message attachment"
            onClose={() => setViewingImage(null)}
          />
        )}

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          {onNavigateToChat && (
            <button
              onClick={() => {
                onNavigateToChat();
                onClose();
              }}
              className="flex-1 py-3 bg-[#8e4585] hover:bg-[#753a6f] text-white font-medium rounded-xl transition-colors"
            >
              Go to Chat
            </button>
          )}
          <button
            onClick={onClose}
            className={`${onNavigateToChat ? '' : 'flex-1'} py-3 px-6 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
